import React from 'react';
import { Stepper, Step, StepLabel, Typography, Paper, styled } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import LensIcon from '@mui/icons-material/Lens';

const GreenStepIcon = styled('span')(({ theme }) => ({
    color: theme.palette.success.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));
const BlueStepIcon = styled('span')(({ theme }) => ({
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));
const GrayStepIcon = styled('span')(({ theme }) => ({
    color: theme.palette.grey[400],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

function CustomStepIcon(props) {
    const { status } = props;
    if (status === 'completed') {
        return (
            <GreenStepIcon>
                <CheckIcon />
            </GreenStepIcon>
        );
    }
    if (status === 'active') {
        return (
            <BlueStepIcon>
                <LensIcon />
            </BlueStepIcon>
        );
    }
    return (
        <GrayStepIcon>
            <RadioButtonUncheckedIcon />
        </GrayStepIcon>
    );
}

const REWORK_STAGES = ['Re-Work', 'Re-Shoot', 'Re-Take'];

const isRtServiceOrder = (order) => {
    if (!order) return true; // Default to true if order not provided

    // Check single service_type (sub-service orders)
    const stName = (order.service_type?.name || (typeof order.service_type === 'string' ? order.service_type : '')).trim().toUpperCase();
    if (stName) {
        if (stName === 'RT' || stName.startsWith('RT ') || stName.startsWith('RT-') || stName.startsWith('RT/') || stName.includes('RADIOGRAPHY')) {
            return true;
        }
    }

    // Check service_types array (parent service orders)
    if (Array.isArray(order.service_types) && order.service_types.length > 0) {
        for (const st of order.service_types) {
            const name = (typeof st === 'string' ? st : st?.name || '').trim().toUpperCase();
            if (name === 'RT' || name.startsWith('RT ') || name.startsWith('RT-') || name.startsWith('RT/') || name.includes('RADIOGRAPHY')) {
                return true;
            }
        }
    }

    // Check order name or title if it contains - RT
    const orderName = (order.name || '').toUpperCase();
    if (orderName.includes(' - RT') || orderName.includes('/RT/')) {
        return true;
    }

    // If order has a defined service type and it wasn't RT, it's non-RT
    if (stName || (Array.isArray(order.service_types) && order.service_types.length > 0)) {
        return false;
    }

    return true;
};

const WorkflowStages = ({ stages = [], history = [], currentStage, onStageClick, serviceOrder }) => {
    const isRt = isRtServiceOrder(serviceOrder);
    const historyIds = new Set((history || []).map(s => s.id));
    const initialStage = stages && stages.length > 0 ? stages.find(s => s.is_initial) : null;
    const effectiveCurrentStage = currentStage || initialStage;

    const visibleStages = React.useMemo(() => {
        if (!stages || stages.length === 0) return [];
        if (isRt) return stages;
        // Non-RT orders: filter out Re-Work, Re-Shoot, Re-Take unless currently active or in history
        return stages.filter(stage => {
            const stageName = (stage.name || '').trim();
            const isRework = REWORK_STAGES.some(r => r.toLowerCase() === stageName.toLowerCase());
            if (!isRework) return true;
            return stage.id === effectiveCurrentStage?.id || historyIds.has(stage.id);
        });
    }, [stages, isRt, effectiveCurrentStage?.id, historyIds]);

    const getStageStatus = (stage) => {
        // If this is the effective current stage (either actual current or initial)
        if (stage.id === effectiveCurrentStage?.id) return 'active';
        // If this stage is in history, it's completed
        if (historyIds.has(stage.id)) return 'completed';
        // Otherwise it's pending
        return 'pending';
    };

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
                Service Order Workflow
            </Typography>
            <Stepper alternativeLabel activeStep={visibleStages.findIndex(s => s.id === effectiveCurrentStage?.id)}>
                {visibleStages.map((stage) => {
                    const status = getStageStatus(stage);
                    const isCurrent = stage.id === effectiveCurrentStage?.id;
                    return (
                        <Step key={stage.id} completed={status === 'completed'}>
                            <StepLabel
                                StepIconComponent={() => <CustomStepIcon status={status} />}
                                onClick={() => onStageClick && onStageClick(stage)}
                                sx={{ cursor: onStageClick ? 'pointer' : 'default' }}
                            >
                                <Typography variant="body2">{stage.name}</Typography>
                                {isCurrent && (
                                    <Typography variant="caption" color="primary" sx={{ fontWeight: 'bold' }}>
                                        (Current Stage)
                                    </Typography>
                                )}
                            </StepLabel>
                        </Step>
                    );
                })}
            </Stepper>
        </Paper>
    );
};

export default WorkflowStages; 